import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const auditedPages = {
  "/erp-software": {
    title: "ERP Software for Complete Business Management | Auditus-iERP",
    description:
      "Auditus-iERP ERP software connects billing, accounting, inventory, GST, POS, sales, purchases, customers, suppliers, expenses, and reports.",
    name: "ERP Software",
    image: "/images/screenshots/dashboard.png",
    keywords: "erp software, business management software, billing accounting inventory software",
  },
  "/billing-software": {
    title: "Billing Software for Faster Business Invoicing | Auditus-iERP",
    description:
      "Auditus-iERP billing software helps businesses create GST-ready invoices, manage products and customers, track payments, connect inventory, and review sales reports.",
    name: "Billing Software",
    image: "/images/screenshots/invoice.png",
    keywords: "billing software, gst billing software, invoice software",
  },
  "/accounting-software": {
    title: "Accounting Software for Business Finance | Auditus-iERP",
    description:
      "Auditus-iERP accounting software connects ledgers, vouchers, sales, purchases, expenses, payments, GST workflows, inventory, billing, and financial reports.",
    name: "Accounting Software",
    image: "/images/screenshots/reports.png",
    keywords: "accounting software, business accounting software, financial management software",
  },
  "/inventory-management-software": {
    title: "Inventory Management Software for Stock Control | Auditus-iERP",
    description:
      "Auditus-iERP inventory management software connects products, stock tracking, purchases, sales, billing, barcode movement, warehouses, batches, reorder levels, and reports.",
    name: "Inventory Management Software",
    image: "/images/screenshots/dashboard.png",
    keywords: "inventory management software, stock management software, inventory software",
  },
  "/pos-software": {
    title: "POS Software for Retail Billing | Auditus-iERP",
    description:
      "Auditus-iERP POS software connects retail billing, barcode workflows, products, customers, payments, inventory, GST-ready invoices, sales tracking, and reports.",
    name: "POS Software",
    image: "/images/screenshots/invoice.png",
    keywords: "pos software, retail pos software, pos billing software",
  },
  "/industries": {
    title: "Industry-Specific ERP and Business Software | Auditus-iERP",
    description:
      "Auditus-iERP industry business software connects billing, POS, inventory, accounting, GST, sales, purchases, customers, suppliers, and reports for retail, supermarket, textile, wholesale, and distribution workflows.",
    name: "Industries",
    image: "/images/screenshots/dashboard.png",
    keywords: "industry erp software, retail business software, supermarket billing software",
  },
  "/gst-software": {
    title: "GST Software for Billing and Reports | Auditus-iERP",
    description:
      "Auditus-iERP GST software supports GST-ready invoices, GST reports, GSTIN details, HSN/SAC handling, e-invoice, e-way bill workflows, billing, accounting, and inventory.",
    name: "GST Software",
    image: "/images/gstdashboard.PNG",
    keywords: "gst software, gst billing software, gst invoice software",
  },
  "/features-solutions": {
    title: "Business Software Features and Solutions | Auditus-iERP",
    description:
      "Explore Auditus-iERP features for billing, accounting, inventory, POS, GST, sales, purchases, customers, suppliers, payments, expenses, and business reports.",
    name: "Features and Solutions",
    image: "/images/screenshots/dashboard.png",
    keywords: "business software features, erp solutions, auditus ierp features",
  },
  "/best-business-software": {
    title: "Best Business Software Buyer Guide | Auditus-iERP",
    description:
      "Compare business software needs for ERP, billing, accounting, inventory, POS, GST, pricing, demos, and fit using verified Auditus-iERP product information.",
    name: "Best Business Software",
    image: "/images/screenshots/dashboard.png",
    keywords: "best business software, business software buyer guide, erp buyer guide",
  },
  "/comparisons": {
    title: "Business Software Comparison Guide | Auditus-iERP",
    description:
      "Compare ERP, billing, accounting, inventory, POS, GST, and integrated business software before choosing the right software for your business.",
    name: "Comparisons",
    image: "/images/screenshots/dashboard.png",
    keywords: "business software comparison, erp vs billing software, pos vs billing software",
  },
  "/india": {
    title: "Business Software for India | Auditus-iERP",
    description:
      "Auditus-iERP business software for India connects ERP, billing, accounting, inventory, POS, GST, sales, purchases, customers, suppliers, payments, and reports.",
    name: "Business Software India",
    image: "/images/screenshots/dashboard.png",
    keywords: "business software india, erp software india, gst billing software india",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Auditus-iERP",
    description:
      "Read the Auditus-iERP Privacy Policy for Google account connection, Gmail send permission, data use, retention, sharing, security, and contact details.",
    name: "Privacy Policy",
    image: "/images/auditlogo.png",
    keywords: "Auditus-iERP privacy policy, Gmail API privacy policy, Auditus IERP privacy",
  },
  "/terms-and-conditions": {
    title: "Auditus-iERP Terms and Conditions",
    description:
      "Read the Auditus-iERP Terms and Conditions for software use, business data responsibilities, Gmail integration, email sending, and contact details.",
    name: "Terms and Conditions",
    image: "/images/auditlogo.png",
    keywords: "Auditus-iERP terms and conditions, Auditus IERP terms, Gmail integration terms",
  },
};

const validStaticPaths = new Set([
  "/",
  "/erp-software",
  "/billing-software",
  "/accounting-software",
  "/inventory-management-software",
  "/pos-software",
  "/industries",
  "/gst-software",
  "/features-solutions",
  "/best-business-software",
  "/comparisons",
  "/india",
  "/privacy-policy",
  "/terms-and-conditions",
  "/productoverview",
  "/whatsnew",
  "/trial",
  "/demo",
  "/pricing",
  "/support",
  "/features",
  "/creation",
  "/auditusierp_demo",
  "/industrial-specification",
  "/download",
  "/brochure",
]);

function isKnownRoute(pathname) {
  return validStaticPaths.has(pathname) || pathname.startsWith("/creation/");
}

function upsertMeta(selector, attrs) {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    document.head.appendChild(tag);
  }
  Object.entries(attrs).forEach(([key, value]) => tag.setAttribute(key, value));
}

function upsertCanonical(pathname) {
  let tag = document.head.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement("link");
    tag.rel = "canonical";
    document.head.appendChild(tag);
  }
  tag.href = pathname;
}

function upsertRouteSchema(page, pathname) {
  let tag = document.getElementById("route-seo-schema");
  if (!tag) {
    tag = document.createElement("script");
    tag.id = "route-seo-schema";
    tag.type = "application/ld+json";
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        name: page.title,
        url: pathname,
        description: page.description,
        inLanguage: "en-IN",
        isPartOf: {
          "@type": "WebSite",
          name: "Auditus-iERP",
          url: "/",
        },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: page.image,
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "/" },
          { "@type": "ListItem", position: 2, name: page.name, item: pathname },
        ],
      },
    ],
  });
}

export default function SeoManager() {
  const { pathname } = useLocation();
  const normalizedPath = pathname.toLowerCase();
  const page = auditedPages[normalizedPath];

  useEffect(() => {
    const schema = document.getElementById("route-seo-schema");
    if (!page) {
      if (schema) schema.remove();
      upsertMeta('meta[name="robots"]', {
        name: "robots",
        content: isKnownRoute(normalizedPath) ? "index, follow" : "noindex, follow",
      });
      return;
    }

    document.title = page.title;
    upsertMeta('meta[name="description"]', { name: "description", content: page.description });
    upsertMeta('meta[name="robots"]', { name: "robots", content: "index, follow" });
    upsertMeta('meta[name="keywords"]', { name: "keywords", content: page.keywords });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: page.title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: page.description });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: normalizedPath });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: page.image });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Auditus-iERP" });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: page.title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: page.description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: page.image });
    upsertCanonical(normalizedPath);
    upsertRouteSchema(page, normalizedPath);
  }, [normalizedPath, page]);

  return null;
}
